import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, Eye, ExternalLink, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeading } from "@/components/site/Section";
import { EmptyState, Loading, ProblemAlert } from "@/components/site/Feedback";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/hooks";
import { openExternal } from "@/lib/external";
import { isMobile } from "@/lib/config";
import { fileSize } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { BlueprintAccessResponse, MemberBlueprintDto } from "@/lib/types";

export function BlueprintPage() {
  usePageTitle("The Blueprint — Tier by Tier Property Book");
  const blueprints = useQuery({ queryKey: ["blueprints"], queryFn: api.blueprints.list });
  const categories = useQuery({ queryKey: ["blueprint-categories"], queryFn: api.blueprints.categories });
  const [preview, setPreview] = useState<BlueprintAccessResponse | null>(null);
  // null = every tier; clicking the selected tier again shows all documents.
  const [tierId, setTierId] = useState<string | null>(null);
  const tier = categories.data?.find((c) => c.id === tierId) ?? null;

  const visible = useMemo(
    () =>
      [...(blueprints.data ?? [])]
        .filter((b) => !tier || b.blueprintCategoryId === tier.id)
        .sort((a, b) => a.order - b.order),
    [blueprints.data, tier],
  );

  return (
    <>
      <PageHero
        eyebrow="Lead magnet"
        title="Tier by Tier: the UK property blueprint"
        subtitle="The book that maps every stage of a portfolio — from your first deposit to commercial joint ventures. Free for members, no cost, no catch."
      />

      <Section>
        <div className="grid gap-14 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <SectionHeading
              eyebrow="Inside the book"
              title="Six tiers, one path"
              subtitle="Each tier ends with a checklist and the exact numbers you should be able to defend before you proceed."
            />
            <div className="mt-10 divide-y divide-border border-y border-border">
              {categories.isPending ? (
                <Loading className="py-5" />
              ) : categories.isError ? (
                <div className="py-5">
                  <ProblemAlert error={categories.error} />
                </div>
              ) : (
                categories.data.map((c) => {
                  const selected = c.id === tierId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setTierId(selected ? null : c.id)}
                      className={cn(
                        "flex w-full flex-col gap-1 border-l-2 border-transparent py-5 text-left transition-colors sm:flex-row sm:gap-8",
                        "-ml-px pl-4 hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:outline-none",
                        selected && "border-gold bg-accent/60 hover:bg-accent/60",
                      )}
                    >
                      <span className="eyebrow w-28 shrink-0 pt-1 text-gold">{c.name}</span>
                      <span className="text-base">{c.description}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="h-fit rounded-lg border border-border bg-card p-8 shadow-elevated lg:sticky lg:top-24">
            <h3 className="text-2xl">Get your copy</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Preview online or download the PDF. A free member account is all you need.
            </p>
            {tier && (
              <p className="mt-4 flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span>
                  <span className="eyebrow text-gold">{tier.name}</span>
                  <span className="text-muted-foreground"> · {tier.description}</span>
                </span>
                <button type="button" className="text-xs underline underline-offset-4" onClick={() => setTierId(null)}>
                  Show all tiers
                </button>
              </p>
            )}

            <div className="mt-6 space-y-4">
              {blueprints.isPending ? (
                <Loading className="py-4" />
              ) : blueprints.isError ? (
                <ProblemAlert error={blueprints.error} />
              ) : visible.length === 0 ? (
                <EmptyState title="Coming soon">
                  {tier ? `The ${tier.name} documents will be available here shortly.` : "The Blueprint will be available here shortly."}
                </EmptyState>
              ) : (
                visible.map((b) => <BlueprintItem key={b.id} blueprint={b} onPreview={setPreview} />)
              )}
            </div>

            <div className="mt-6 border-t border-border pt-6">
              <p className="text-sm text-muted-foreground">
                Ready to go further? The Academy Portal adds the video library and AI Deal Analyzer.
              </p>
              <Button asChild variant="outlineGold" size="sm" className="mt-4 w-full">
                <Link to="/academy">See the portal</Link>
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="flex h-[90vh] max-w-5xl flex-col gap-3 p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium">{preview?.fileName}</DialogTitle>
            <DialogDescription className="flex flex-wrap gap-4">
              <button className="inline-flex items-center gap-1 underline-offset-4 hover:underline" onClick={() => preview && openExternal(preview.previewUrl)}>
                <ExternalLink className="size-3.5" /> Open in a new tab
              </button>
              <button className="inline-flex items-center gap-1 underline-offset-4 hover:underline" onClick={() => preview && openExternal(preview.downloadUrl)}>
                <Download className="size-3.5" /> Download
              </button>
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <iframe
              title={preview.fileName}
              src={preview.previewUrl}
              className="min-h-0 w-full flex-1 rounded-md border border-border bg-muted"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function BlueprintItem({
  blueprint,
  onPreview,
}: {
  blueprint: MemberBlueprintDto;
  onPreview: (access: BlueprintAccessResponse) => void;
}) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const access = useMutation({
    mutationFn: () => api.blueprints.access(blueprint.id),
    onError: (e) => toast.error("The document is not available", { description: errorMessage(e) }),
  });

  const run = async (action: "preview" | "download") => {
    if (!isAuthenticated) {
      await navigate({ to: "/signup", search: { redirect: "/blueprint" } });
      return;
    }
    const result = await access.mutateAsync().catch(() => null);
    if (!result) return;
    if (action === "download") openExternal(result.downloadUrl);
    // Mobile: no embedded viewer in the HybridWebView; the host opens the PDF.
    else if (isMobile) openExternal(result.previewUrl);
    else onPreview(result);
  };

  return (
    <div className="rounded-md border border-border p-4">
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 size-5 shrink-0 text-gold" />
        <div className="min-w-0">
          <p className="font-medium">{blueprint.name}</p>
          {blueprint.description && <p className="mt-1 text-sm text-muted-foreground">{blueprint.description}</p>}
          {blueprint.fileName && (
            <p className="mt-1 text-xs text-muted-foreground">
              PDF{blueprint.sizeBytes ? ` · ${fileSize(blueprint.sizeBytes)}` : ""}
            </p>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outlineGold" size="sm" disabled={access.isPending || !blueprint.fileName} onClick={() => void run("preview")}>
          {access.isPending ? <Loader2 className="animate-spin" /> : <Eye />}
          Preview
        </Button>
        <Button variant="gold" size="sm" disabled={access.isPending || !blueprint.fileName} onClick={() => void run("download")}>
          <Download />
          Download
        </Button>
      </div>
      {!isAuthenticated && (
        <p className="mt-3 text-xs text-muted-foreground">
          <Link to="/login" search={{ redirect: "/blueprint" }} className="underline underline-offset-4">
            Sign in
          </Link>{" "}
          or create a free account to read it.
        </p>
      )}
    </div>
  );
}
